/**
 * SystemCaveatsBanner — makes the platform's *serious caveats* impossible
 * to miss, so the trader never tests assuming "all is sorted".
 *
 * The existing /health check tests CONNECTIVITY ("can I reach T212?") and
 * happily returns verdict:"ok" while the system genuinely can't trade —
 * e.g. the demo account is out of buying power, orders are stuck after a
 * market-closed placement, or the sentiment LLM is offline so a COMPASS
 * factor is blind. This banner re-derives SEVERITY from the raw signals
 * (broker free cash, provider state, OMS order states) and shows red/amber
 * caveats with a plain-English "what it means", on the surfaces the trader
 * actually looks at. It auto-clears when the underlying condition clears.
 *
 * Data: /health/integrations (broker cash + provider state) + OMS orders
 * (execution health). No new backend — derivation lives here on purpose so
 * a stale API verdict can't mask a real problem.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";

type Severity = "red" | "amber";
type Caveat = { sev: Severity; title: string; detail: string };

const RED = "#ef4444";
const AMBER = "#f59e0b";

// Below this much free broker cash, a multi-name strategy basket will start
// hitting insufficient-free-for-stocks rejections — treat as a hard caveat.
const LOW_CASH_USD = 2_000;

/** Pull "free 248.73 USD" / "available 10000908 GBP" out of a provider
 * detail string. Returns {amount, ccy} or null. */
function parseCash(detail: string): { amount: number; ccy: string } | null {
  const m = detail.match(/(?:free|available)\s+([\d,]+(?:\.\d+)?)\s*([A-Z]{3})/i);
  if (!m) return null;
  return { amount: Number(m[1].replace(/,/g, "")), ccy: m[2].toUpperCase() };
}

/** Turn a raw broker rejection reason into a plain-English phrase. The
 * trader shouldn't have to decode `HTTP 400 {"type":"/api-errors/...}`. */
function prettifyReason(raw: string | null | undefined): string {
  const s = raw || "";
  if (/insufficient-free/.test(s)) return "broker out of buying power";
  if (/selling-equity-not-owned/.test(s)) return "tried to sell equity not owned (no shorting on T212)";
  if (/instrument-disabled/.test(s)) return "instrument disabled at broker";
  if (/quantity-precision-mismatch/.test(s)) return "quantity precision mismatch";
  if (/market[_\s]?closed/i.test(s)) return "market closed";
  if (/buying_power_floor/.test(s)) return "below buying-power floor (gate)";
  if (/broker_capability/.test(s)) return "broker can't trade this instrument";
  // Local engine risk-gate codes (never reach the OMS).
  if (/max_position_value/.test(s)) return "position-value cap (risk gate)";
  if (/max_position_pct/.test(s)) return "position-% cap (risk gate)";
  if (/position_cap|strategy_position_cap/.test(s)) return "position-count cap (risk gate)";
  if (/short_disallowed/.test(s)) return "short blocked (long-only)";
  if (/halted/.test(s)) return "strategy halted (risk gate)";
  if (/entity-not-found|does not exist|Ticker does not exist/i.test(s)) return "unknown ticker at broker";
  // Generic /api-errors/<type> → humanise the type token.
  const m = s.match(/api-errors\/([a-z-]+)/i);
  if (m) return m[1].replace(/-/g, " ");
  return s.slice(0, 50) || "unknown";
}

export function SystemCaveatsBanner() {
  const [caveats, setCaveats] = useState<Caveat[]>([]);
  const [open, setOpen] = useState(true);

  const compute = useCallback(async () => {
    const found: Caveat[] = [];

    // ── Broker + provider readiness ────────────────────────────────
    try {
      const h = await api.integrationsHealth();
      // The LLM (Ollama) runs on the MAC worker, not next to the EC2 backend —
      // so the backend's active probe to host.docker.internal ALWAYS fails there
      // and falsely flags the sentiment factor offline. The cache-derived
      // ollama health (populated by the worker that actually scores) is the
      // truth. If ANY llm/ollama provider is healthy, the factor is NOT blind.
      const llmAlive = h.providers.some(
        (p) => /llm|ollama/i.test(p.provider) && p.status === "ok");
      for (const p of h.providers) {
        // Broker buying power — the zero-fill root cause. status can be
        // "ok" (connectivity) while free cash is depleted.
        const cash = parseCash(p.detail);
        const isBroker = /trading212|ig/i.test(p.provider);
        if (isBroker && cash && cash.amount < LOW_CASH_USD && cash.ccy === "USD") {
          found.push({
            sev: "red",
            title: `${p.label}: buying power ${cash.ccy} ${cash.amount.toFixed(0)}`,
            detail:
              "Strategy BUY baskets will be rejected (insufficient-free-for-stocks). "
              + "Top up / reset the demo account or add a capital gate before expecting fills.",
          });
        }
        if (p.status === "down") {
          found.push({ sev: "red", title: `${p.label} DOWN`, detail: p.detail });
        } else if (p.status === "degraded") {
          found.push({ sev: "amber", title: `${p.label} degraded`, detail: p.detail });
        } else if (p.status === "disabled") {
          // LLM disabled => the sentiment COMPASS factor (10% weight) is
          // blind. Worth an amber so the trader knows the score is partial.
          const isLlm = /llm|ollama/i.test(p.provider);
          // Suppress the false "sentiment offline" alarm: the worker's LLM is up
          // (a healthy llm/ollama provider exists) — it's only the EC2-side probe
          // that can't reach the Mac's Ollama. Don't cry wolf on a working factor.
          if (!(isLlm && llmAlive)) {
            found.push({
              sev: "amber",
              title: isLlm ? "Sentiment factor offline (LLM unreachable)" : `${p.label} disabled`,
              detail: isLlm
                ? "News-sentiment scoring needs the Mac LLM. Until it's reachable, the sentiment factor defaults neutral (5/10) — 10% of every COMPASS score is blind."
                : p.detail,
            });
          }
        }
      }
    } catch {
      found.push({
        sev: "amber",
        title: "Integration health unavailable",
        detail: "Couldn't reach /health/integrations — broker/provider state is unknown.",
      });
    }

    // ── Execution health from the OMS ──────────────────────────────
    try {
      const { orders } = await api.omsOrders(undefined, 500);
      const now = Date.now();
      const within72h = (iso: string) =>
        iso && now - new Date(iso).getTime() < 72 * 3600 * 1000;
      const within24h = (iso: string) =>
        iso && now - new Date(iso).getTime() < 24 * 3600 * 1000;

      const stuckSubmitted = orders.filter(
        (o) => o.state === "SUBMITTED" && o.placedBy === "STRATEGY_AUTO",
      );
      if (stuckSubmitted.length > 0) {
        found.push({
          sev: "red",
          title: `${stuckSubmitted.length} strategy orders stuck SUBMITTED`,
          detail:
            "Accepted by the broker but never filled — typically placed after the market close, "
            + "or the fill poller isn't reconciling them. Check on the next open.",
        });
      }

      const recentRejects = orders.filter(
        (o) => o.state === "REJECTED" && within24h(o.createdAtUtc),
      );
      if (recentRejects.length > 0) {
        // Group by plain-English reason and surface the dominant one(s) so
        // the trader sees WHY, not just a count.
        const byReason = new Map<string, number>();
        for (const o of recentRejects) {
          const r = prettifyReason(o.cancelledReason);
          byReason.set(r, (byReason.get(r) ?? 0) + 1);
        }
        const ranked = [...byReason.entries()].sort((a, b) => b[1] - a[1]);
        const summary = ranked.map(([r, n]) => `${n}× ${r}`).join(" · ");
        found.push({
          sev: "red",
          title: `${recentRejects.length} order rejection${recentRejects.length === 1 ? "" : "s"} in last 24h`,
          detail: `${summary}. ${ranked[0][0].includes("buying power") ? "Strategy is firing orders it can't fund. " : ""}See the /oms rejection histogram for detail.`,
        });
      }

      // No real strategy fill in the last 72h while signals are firing.
      const recentStrategyFill = orders.some(
        (o) => o.state === "FILLED" && o.placedBy === "STRATEGY_AUTO"
          && o.strategyId !== "reconcile_from_broker" && within72h(o.lastStateChangeAtUtc),
      );
      const recentStrategyActivity = orders.some(
        (o) => o.placedBy === "STRATEGY_AUTO" && within72h(o.createdAtUtc),
      );
      if (recentStrategyActivity && !recentStrategyFill) {
        found.push({
          sev: "amber",
          title: "No strategy fills in 72h despite activity",
          detail: "Strategies placed orders but none filled — usually the buying-power or market-closed caveat above.",
        });
      }
    } catch {
      /* OMS unreachable — the provider caveat above already covers connectivity */
    }

    // ── Local risk-gate rejections (invisible to the OMS) ──────────────
    // A strategy's order can be killed by the Mac engine's LOCAL risk gate
    // (e.g. max_position_value) BEFORE it ever reaches the OMS — so the OMS
    // block above can't see it and an entire desk can be 100%-blocked while
    // every other surface looks fine. The snapshot now carries
    // recent_rejections; surface today's per strategy with the reason.
    try {
      const snaps = await api.paperSnapshots();
      const today = new Date().toISOString().slice(0, 10);
      const live = snaps.filter((s) => (s.sessionLabel || "").endsWith(today));
      const details = await Promise.all(
        live.map((s) =>
          api.paperSnapshot(s.sessionLabel).then((d) => d).catch(() => null)),
      );
      for (const d of details) {
        const strategies = ((d as Record<string, unknown> | null)?.strategies ?? []) as Array<{
          strategy_id?: string;
          recent_rejections?: Array<{ ts?: string; code?: string; reason?: string; symbol?: string }>;
        }>;
        for (const st of strategies) {
          const todays = (st.recent_rejections ?? []).filter((r) => (r.ts || "").slice(0, 10) === today);
          if (todays.length === 0) continue;
          const byCode = new Map<string, number>();
          for (const r of todays) {
            const c = prettifyReason(r.code || r.reason);
            byCode.set(c, (byCode.get(c) ?? 0) + 1);
          }
          const ranked = [...byCode.entries()].sort((a, b) => b[1] - a[1]);
          const summary = ranked.map(([c, n]) => `${n}× ${c}`).join(" · ");
          found.push({
            sev: "red",
            title: `${st.strategy_id}: ${todays.length} order${todays.length === 1 ? "" : "s"} blocked by the risk gate today`,
            detail: `${summary}. These were rejected LOCALLY (never reached the broker), so the desk can look idle while it's actually 100% blocked. Fix the gate/sizing or the symbol mapping.`,
          });
        }
      }
    } catch {
      /* snapshots unreachable — non-fatal, the other caveats still render */
    }

    // ── Bar-cache harvest readiness ────────────────────────────────
    // An empty bar cache means strategy backtests + the capital gate are
    // running on yfinance's 7-day 1m cap. Not a blocking live-money risk
    // but we want it visible at all times until the first IBKR harvest
    // completes. Uses bar-cache/health (already deployed, zero auth) so
    // no dependency on the new /ibkr/* endpoints.
    try {
      const { health } = await api.barCacheHealth();
      if (health.length === 0) {
        found.push({
          sev: "amber",
          title: "Bar cache empty — IBKR harvest not started",
          detail:
            "No historical bars have been written yet. Strategies using 1m resolution " +
            "fall back to yfinance's 7-day window, which severely limits backtests and " +
            "the capital gate. Go to Settings › Data Health › IBKR Bar Harvester to " +
            "trigger the initial harvest (TWS must be running; migration 045 + backend " +
            "redeploy are prerequisites).",
        });
      }
    } catch {
      /* non-fatal — /bar-cache/health unreachable; skip */
    }

    setCaveats(found);
  }, []);

  useEffect(() => {
    void compute();
    const t = setInterval(compute, 60_000);
    return () => clearInterval(t);
  }, [compute]);

  if (caveats.length === 0) return null;

  const hasRed = caveats.some((c) => c.sev === "red");
  const tone = hasRed ? RED : AMBER;
  const reds = caveats.filter((c) => c.sev === "red").length;
  const ambers = caveats.length - reds;

  return (
    <div
      style={{
        border: `1px solid ${tone}`,
        borderLeft: `4px solid ${tone}`,
        borderRadius: 8,
        background: hasRed ? "rgba(239,68,68,0.07)" : "rgba(245,158,11,0.07)",
        padding: "10px 14px",
        marginBottom: 12,
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ fontSize: 14 }}>{hasRed ? "🔴" : "🟡"}</span>
        <strong style={{ fontSize: 13, color: tone }}>
          {caveats.length} system caveat{caveats.length === 1 ? "" : "s"}
        </strong>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {reds > 0 && `${reds} blocking`}{reds > 0 && ambers > 0 && " · "}
          {ambers > 0 && `${ambers} degraded`}
          {" — signals/fills may be unreliable until cleared"}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>
          {open ? "▾ hide" : "▸ show"}
        </span>
      </div>
      {open && (
        <ul style={{ margin: "8px 0 2px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
          {caveats.map((c, i) => (
            <li key={i} style={{ display: "flex", gap: 8, fontSize: 12, lineHeight: 1.45 }}>
              <span style={{ color: c.sev === "red" ? RED : AMBER, fontWeight: 700, flexShrink: 0 }}>
                {c.sev === "red" ? "●" : "▲"}
              </span>
              <span>
                <strong style={{ color: "var(--text)" }}>{c.title}.</strong>{" "}
                <span style={{ color: "var(--text-dim)" }}>{c.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>
        Re-derived from broker cash + OMS state every 60s (not the API's
        connectivity-only verdict). <Link to="/health" style={{ color: "var(--text-muted)" }}>Full health →</Link>
      </div>
    </div>
  );
}
